import { useQuery } from '@tanstack/react-query';

import { createPublicRocClient } from '@/adapters/publicROCApi';
import type { RecipientReferralView } from '@/types/roc/models/RecipientReferralView';
import { ResponseError } from '@/types/roc/runtime';

import { getPublicReferralAccessQueryKey } from '../queryKeys';

export interface PublicReferralAccessData {
  referral: RecipientReferralView;
}

export function getPublicReferralAccessErrorMessage(
  error: unknown,
  fallback: string
) {
  if (error instanceof ResponseError) {
    if (error.response.status === 401 || error.response.status === 403) {
      return 'This referral link is invalid or no longer available.';
    }
    if (error.response.status === 404) {
      return 'This referral could not be found.';
    }
  }

  return fallback;
}

export function usePublicReferralAccess(
  invitationToken: string | undefined,
  sessionToken: string | undefined,
  referralId: string | undefined
) {
  return useQuery({
    queryKey: getPublicReferralAccessQueryKey(invitationToken, sessionToken),
    queryFn: async (): Promise<PublicReferralAccessData> => {
      if (!invitationToken || !sessionToken || !referralId) {
        throw new Error('Missing verified referral access.');
      }

      const referralClient = createPublicRocClient(
        sessionToken,
        invitationToken
      );
      const referral = await referralClient.getRecipientReferralView({
        referralId
      });

      return { referral };
    },
    enabled: Boolean(invitationToken && sessionToken && referralId),
    retry: false
  });
}
